import { useState, useEffect, useMemo, useCallback } from "react";
import { ApolloError, useQuery, useMutation } from "@apollo/client";
import {
    getCorporationAndMillBackOffice,
    getCorporations,
    insertCorporationWithAdmin,
    upsertCorporationData,
    activateCorporateModuleLicense,
    getCorporationDetail,
    getCountries,
    getStatesByCountryId,
    getCitiesByStateId,
    GetCorporationAdmins,
    deactivateOrReactivateCorporateModuleLicense,
    getCorporationCountryCode,
    upsertCorporationWithAdminUser,
    getCorporateAllDivisionAllRegionsAllMills,
    getAllKeySuccessFactors,
    getAllFailureTypes,
    updateCorporateDashboardFeature,
} from "../../api/queries/corporationsAPI";
import {
    ICity,
    ICorporation,
    ICountry,
    IMill,
    IState,
} from "../../pages/Osorno/OsornoSetupDashboard/Corporations/CorporationsAPIInterfaces";
import {
    ICorporationsEntity,
    IDowntimeSummaryByFailureCode,
    IDowntimeSummaryByFailureType,
    IDowntimeSummaryBySecondaryFailureCodeTotals,
    IDowntimeSummaryTotalsByCodeAllMills,
    IDowntimeSummaryTotalsByTypeAllMills,
    IUser,
    TrailingTrendPeriodOptionsEnum,
} from "./CorporationsEntityInterfaces";
import { getCorporateDivisionsRegionsMills } from "api/queries/AppDrawerAPI";
import { ICorporateDashboardFilters } from "pages/Corporate/CorporateDashboard/useCorporateDashboardFilters";
import { END_OF_SHIFT_FAILURE_CODE_NAME, MEASURE_METRICS, SummaryOption, allElements } from "constants/constants";
import { getEndOfDateRange, getStartOfDateRange } from "utilities/utilities";
import {
    getDowntimeSummaryByFailureCodeCorporateDashboard,
    getDowntimeSummaryByFailureTypeCorporateDashboard,
    getDowntimeSummaryBySecondaryFailureCodeTotalsCorporateDashboard,
    getDowntimeSummaryFailureCodeByMillsCorporateDashboard,
    getDowntimeSummaryFailureTypeByMillsCorporateDashboard,
    getDowntimeSummarySecondaryFailureCodeByMillsCorporateDashboard,
} from "api/queries/corporateDashboardAPI";
import moment from "moment";
import { IKeySuccessFactor } from "entities/RCA/RCALibraryEntity/RCALibraryEntityInterfaces";
import { IFailureType } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";

export const CorporationsEntity: ICorporationsEntity = {
    GetCorporations: (user_type_id: number) => {
        const { loading, error, data, refetch } = useQuery(getCorporations, { variables: { user_type_id } });
        const [corporations, setCorporations] = useState<ICorporation[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.corporate) {
                const corporationsArray: ICorporation[] = data.corporate;
                setCorporations(corporationsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, corporations, apolloQueryError, refetch };
    },
    GetCorporationCountryCode: (corporate_id: number) => {
        const { loading, error, data, refetch } = useQuery(getCorporationCountryCode, { variables: { corporate_id } });
        const [corporationCountryCode, setCorporationCountryCode] = useState<string>("");
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const corporationCountryCode: string = data.corporate[0].country?.code;
                setCorporationCountryCode(corporationCountryCode);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, corporationCountryCode, apolloQueryError, refetch };
    },
    GetCountries: () => {
        const { loading, error, data, refetch } = useQuery(getCountries);
        const [countries, setCountries] = useState<ICountry[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const countriesArray: ICountry[] = data.country;
                setCountries(countriesArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, countries, apolloQueryError, refetch };
    },
    GetStates: (country_id: number) => {
        const { loading, error, data, refetch } = useQuery(getStatesByCountryId, { variables: { country_id } });
        const [states, setStates] = useState<IState[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const statesArray: IState[] = data.state;
                setStates(statesArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, states, apolloQueryError, refetch };
    },
    GetCities: (state_id: number) => {
        const { loading, error, data, refetch } = useQuery(getCitiesByStateId, { variables: { state_id } });
        const [cities, setCities] = useState<ICity[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const citiesArray: ICity[] = data.city;
                setCities(citiesArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, cities, apolloQueryError, refetch };
    },
    InsertCorporationWithAdmin: () => {
        const [addCorporationWithAdmin, { error }] = useMutation(insertCorporationWithAdmin);

        return { apolloMutationError: error, addCorporationWithAdmin };
    },
    UpsertCorporationWithAdminMaderaBackoffice: () => {
        const [upsertCorporationWithAdmin, { error }] = useMutation(upsertCorporationWithAdminUser);

        return { apolloMutationError: error, upsertCorporationWithAdmin };
    },
    GetCorporationAndMillBackOffice: (millId?: number) => {
        const { loading, error, data, refetch } = useQuery(
            getCorporationAndMillBackOffice,
            useMemo(
                () => ({
                    fetchPolicy: "no-cache",
                    variables: {
                        where: {
                            ...(millId
                                ? {
                                      mill_id: { _eq: millId },
                                  }
                                : {}),
                        },
                        currentDate: new Date().toISOString(),
                    },
                }),
                [millId],
            ),
        );

        return {
            loading,
            corporation: data?.corporate?.[0],
            mill: data?.mill?.[0],
            error,
            refetch,
        };
    },
    UpsertCorporateDataOnboardingBO: () => {
        const [upsertCorporateData, { error }] = useMutation(upsertCorporationData);

        return { apolloMutationError: error, upsertCorporateData };
    },
    GetMenuCorporateDivisionsRegionsMills: () => {
        const { loading, data, error, refetch } = useQuery(getCorporateDivisionsRegionsMills);

        const [corporations, setCorporations] = useState<ICorporation[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);
        const [millsWithNoDivision, setMillsWithNoDivision] = useState<{ name: string; mill_id: number }[]>([]);

        useEffect(() => {
            if (data?.corporate) {
                const corporationsArray: ICorporation[] = data.corporate;
                setCorporations(corporationsArray);
            }
            if (data?.mill) {
                const millsArray: { name: string; mill_id: number }[] = data.mill;
                setMillsWithNoDivision(millsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, corporations, millsWithNoDivision, error: apolloQueryError, refetch };
    },
    ActivateCorporateModuleLicenseOsornoBO: () => {
        const [activateCorporateModule, { error }] = useMutation(activateCorporateModuleLicense);

        return { apolloMutationError: error, activateCorporateModule };
    },
    DeactivateOrReactivateCorporateModuleLicenseOsornoBO: () => {
        const [deactivateOrReactivateCorporateModule, { error }] = useMutation(
            deactivateOrReactivateCorporateModuleLicense,
        );

        return { apolloMutationError: error, deactivateOrReactivateCorporateModule };
    },
    GetCorporationDetail: (corporate_id: number) => {
        const { loading, data, error, refetch } = useQuery(getCorporationDetail, {
            variables: { corporate_id },
        });
        const [corporate, setCorporate] = useState<ICorporation | undefined>(undefined);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);
        const [millsWithNoDivision, setMillsWithNoDivision] = useState<IMill[]>([]);

        useEffect(() => {
            if (data?.corporate) {
                const corporationObject: ICorporation = data?.corporate?.[0];
                setCorporate(corporationObject);
            }
            if (data?.mill) {
                const millsArray: IMill[] = data.mill;
                setMillsWithNoDivision(millsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);
        return {
            loading,
            corporate,
            millsWithNoDivision,
            error: apolloQueryError,
            refetch,
        };
    },
    GetCorporationAdmins: (corporate_id: number, user_type_id: number) => {
        const { loading, data, error, refetch } = useQuery(GetCorporationAdmins, {
            variables: { corporate_id, user_type_id },
        });

        let corporationAdmins: IUser[] = [];

        if (data) {
            corporationAdmins = data?.corporate?.[0].users;
        }

        return {
            loading,
            corporationAdmins,
            error,
            refetch,
        };
    },
    GetDowntimeSummaryByFailureTypeCorporateDashboard: (filters: ICorporateDashboardFilters) => {
        const { loading, error, data, refetch } = useQuery(getDowntimeSummaryByFailureTypeCorporateDashboard, {
            variables: {
                where: {
                    _or: [
                        {
                            failure_code: {
                                _or: [
                                    {
                                        code: { _neq: END_OF_SHIFT_FAILURE_CODE_NAME },
                                    },
                                    {
                                        code: { _is_null: true },
                                    },
                                ],
                            },
                        },
                        {
                            failure_code_id: { _is_null: true },
                        },
                    ],
                    measure_metric_id: { _eq: MEASURE_METRICS.DOWNTIME },
                    created_at: {
                        _gte: getStartOfDateRange(filters.startDate as Date),
                        _lte: getEndOfDateRange(filters.endDate as Date),
                    },
                    machine_center: {
                        ...(filters.machineCenter?.length! > 0 &&
                            !filters.machineCenter?.includes(allElements) && {
                                machine_center_id: {
                                    _in: filters.machineCenter,
                                },
                            }),
                        production_line: {
                            ...(filters.productionLine?.length! > 0 &&
                                !filters.productionLine?.includes(allElements) && {
                                    production_line_id: {
                                        _in: filters.productionLine,
                                    },
                                }),
                            mill: {
                                ...(filters.division !== allElements && {
                                    division_id: {
                                        _eq: filters.division,
                                    },
                                }),
                                ...(filters.region !== allElements && {
                                    region_id: {
                                        _eq: filters.region,
                                    },
                                }),
                            },
                        },
                    },
                },
            },
        });
        const [downtimeSummaryByFailureType, setDowntimeSummaryByFailureType] = useState<
            IDowntimeSummaryByFailureType[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.failure_type) {
                const downtimeSummaryByFailureTypeArray: IDowntimeSummaryByFailureType[] = data.failure_type;
                //Here we have to filter the failure types that have seconds null because Hasura doesn't support filtering on aggregate fields directly.
                const downtimeSummaryByFailureTypeArrayFiltered = downtimeSummaryByFailureTypeArray.filter(
                    (downtimeSummary) =>
                        downtimeSummary.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.seconds > 0,
                );
                setDowntimeSummaryByFailureType(downtimeSummaryByFailureTypeArrayFiltered);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, downtimeSummaryByFailureType, apolloQueryError, refetch };
    },
    GetDowntimeSummaryByFailureCodeCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        failureTypeId?: number,
    ) => {
        const { loading, error, data, refetch } = useQuery(getDowntimeSummaryByFailureCodeCorporateDashboard, {
            variables: {
                failure_type_id: failureTypeId,
                where: {
                    _or: [
                        {
                            failure_code: {
                                _or: [
                                    {
                                        code: { _neq: END_OF_SHIFT_FAILURE_CODE_NAME },
                                    },
                                    {
                                        code: { _is_null: true },
                                    },
                                ],
                            },
                        },
                        {
                            failure_code_id: { _is_null: true },
                        },
                    ],
                    measure_metric_id: { _eq: MEASURE_METRICS.DOWNTIME },
                    created_at: {
                        _gte: getStartOfDateRange(filters.startDate as Date),
                        _lte: getEndOfDateRange(filters.endDate as Date),
                    },
                    machine_center: {
                        ...(filters.machineCenter?.length! > 0 &&
                            !filters.machineCenter?.includes(allElements) && {
                                machine_center_id: {
                                    _in: filters.machineCenter,
                                },
                            }),
                        production_line: {
                            ...(filters.productionLine?.length! > 0 &&
                                !filters.productionLine?.includes(allElements) && {
                                    production_line_id: {
                                        _in: filters.productionLine,
                                    },
                                }),
                            mill: {
                                ...(filters.division !== allElements && {
                                    division_id: {
                                        _eq: filters.division,
                                    },
                                }),
                                ...(filters.region !== allElements && {
                                    region_id: {
                                        _eq: filters.region,
                                    },
                                }),
                            },
                        },
                    },
                },
            },
        });
        const [downtimeSummaryByFailureCode, setDowntimeSummaryByFailureCode] = useState<
            IDowntimeSummaryByFailureCode[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.failure_code) {
                const downtimeSummaryByFailureCodeArray: IDowntimeSummaryByFailureCode[] = data.failure_code;
                //Here we have to filter the failure types that have seconds null because Hasura doesn't support filtering on aggregate fields directly.
                const downtimeSummaryByFailureCodeArrayFiltered = downtimeSummaryByFailureCodeArray.filter(
                    (downtimeSummary) =>
                        downtimeSummary.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.seconds > 0,
                );
                setDowntimeSummaryByFailureCode(downtimeSummaryByFailureCodeArrayFiltered);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, downtimeSummaryByFailureCode, apolloQueryError, refetch };
    },
    GetDowntimeSummaryBySecondaryFailureCodeTotalsCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        failureCodeId?: number,
    ) => {
        const { loading, error, data, refetch } = useQuery(
            getDowntimeSummaryBySecondaryFailureCodeTotalsCorporateDashboard,
            {
                variables: {
                    start_date: getStartOfDateRange(filters.startDate as Date),
                    end_date: getEndOfDateRange(filters.endDate as Date),
                    where: {
                        failure_code_id: { _eq: failureCodeId },
                        machine_center: {
                            ...(filters.machineCenter?.length! > 0 &&
                                !filters.machineCenter?.includes(allElements) && {
                                    machine_center_id: {
                                        _in: filters.machineCenter,
                                    },
                                }),
                            production_line: {
                                ...(filters.productionLine?.length! > 0 &&
                                    !filters.productionLine?.includes(allElements) && {
                                        production_line_id: {
                                            _in: filters.productionLine,
                                        },
                                    }),
                                mill: {
                                    ...(filters.division !== allElements && {
                                        division_id: {
                                            _eq: filters.division,
                                        },
                                    }),
                                    ...(filters.region !== allElements && {
                                        region_id: {
                                            _eq: filters.region,
                                        },
                                    }),
                                },
                            },
                        },
                    },
                },
            },
        );
        const [downtimeSummaryBySecondaryFailureCodeTotals, setDowntimeSummaryBySecondaryFailureCodeTotals] = useState<
            IDowntimeSummaryBySecondaryFailureCodeTotals[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.downtimes) {
                const downtimeSummaryBySecondaryFailureCodeArray: IDowntimeSummaryBySecondaryFailureCodeTotals[] =
                    data.downtimes;
                //Here we have to filter the failure types that have seconds null because Hasura doesn't support filtering on aggregate fields directly.
                const downtimeSummaryBySecondaryFailureCodeArrayFiltered =
                    downtimeSummaryBySecondaryFailureCodeArray.filter(
                        (downtimeSummary) => downtimeSummary.total_seconds > 0,
                    );
                setDowntimeSummaryBySecondaryFailureCodeTotals(downtimeSummaryBySecondaryFailureCodeArrayFiltered);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return {
            loading,
            downtimeSummaryBySecondaryFailureCodeTotals,
            apolloQueryError,
            refetch,
        };
    },
    GetTotalDowntimeSummaryByTypeAllMillsCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        summaryOption: SummaryOption,
        id?: number,
    ) => {
        const { loading, error, data, refetch } = useQuery(getDowntimeSummaryFailureTypeByMillsCorporateDashboard, {
            variables: {
                start_date: getStartOfDateRange(filters.startDate as Date),
                end_date: getEndOfDateRange(filters.endDate as Date),
                start_date_trailing_trend:
                    TrailingTrendPeriodOptionsEnum.ONE_MONTH === filters.trailingTrendPeriod
                        ? moment
                              .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                              .subtract(1, "months")
                              .startOf("day")
                              .toDate()
                        : TrailingTrendPeriodOptionsEnum.THIRTEEN_WEEK === filters.trailingTrendPeriod
                        ? moment
                              .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                              .subtract(13, "weeks")
                              .startOf("day")
                              .toDate()
                        : TrailingTrendPeriodOptionsEnum.SIX_MONTH === filters.trailingTrendPeriod
                        ? moment
                              .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                              .subtract(6, "months")
                              .startOf("day")
                              .toDate()
                        : moment
                              .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                              .subtract(1, "years")
                              .startOf("day")
                              .toDate(),
                end_date_trailing_trend: getEndOfDateRange(filters.startDate as Date),
                where: {
                    ...(summaryOption === SummaryOption.BY_TYPE ? { failure_type_id: { _eq: id } } : {}),
                    machine_center: {
                        ...(filters.machineCenter?.length! > 0 &&
                            !filters.machineCenter?.includes(allElements) && {
                                machine_center_id: {
                                    _in: filters.machineCenter,
                                },
                            }),
                        production_line: {
                            ...(filters.productionLine?.length! > 0 &&
                                !filters.productionLine?.includes(allElements) && {
                                    production_line_id: {
                                        _in: filters.productionLine,
                                    },
                                }),
                            mill: {
                                ...(filters.division !== allElements && {
                                    division_id: {
                                        _eq: filters.division,
                                    },
                                }),
                                ...(filters.region !== allElements && {
                                    region_id: {
                                        _eq: filters.region,
                                    },
                                }),
                            },
                        },
                    },
                },
            },
        });
        const [downtimeSummaryAllMills, setDowntimeSummaryAllMills] = useState<IDowntimeSummaryTotalsByTypeAllMills[]>(
            [],
        );
        const [downtimeSummaryTrailingPeriod, setDowntimeSummaryTrailingPeriod] = useState<
            IDowntimeSummaryTotalsByTypeAllMills[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.baseDowntimes) {
                const downtimeSummaryByFailureTypeAllMillsArray: IDowntimeSummaryTotalsByTypeAllMills[] =
                    data.baseDowntimes;
                //Here we have to filter the failure types that have seconds null because Hasura doesn't support filtering on aggregate fields directly.
                const downtimeSummaryByFailureTypeAllMillsArrayFiltered: IDowntimeSummaryTotalsByTypeAllMills[] =
                    downtimeSummaryByFailureTypeAllMillsArray.filter((summary) => summary.total_seconds > 0);
                setDowntimeSummaryAllMills(downtimeSummaryByFailureTypeAllMillsArrayFiltered);
                if (data?.trailingTrendDowntimes) {
                    const downtimeSummaryTrailingPeriodArray: IDowntimeSummaryTotalsByTypeAllMills[] =
                        data.trailingTrendDowntimes;
                    //Here we have to filter the failure types that have seconds null because Hasura doesn't support filtering on aggregate fields directly.
                    const downtimeSummaryTrailingPeriodArrayFiltered: IDowntimeSummaryTotalsByTypeAllMills[] =
                        downtimeSummaryTrailingPeriodArray.filter((summary) => summary.total_seconds > 0);
                    setDowntimeSummaryTrailingPeriod(downtimeSummaryTrailingPeriodArrayFiltered);
                }
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return {
            loading,
            downtimeSummaryAllMills,
            downtimeSummaryTrailingPeriod,
            apolloQueryError,
            refetch,
        };
    },
    GetTotalDowntimeSummaryByCodeOrSecondaryCodeAllMillsCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        summaryOption: SummaryOption,
        id?: number,
        codeId?: number,
    ) => {
        const { loading, error, data, refetch } = useQuery(
            summaryOption === SummaryOption.BY_CODE
                ? getDowntimeSummaryFailureCodeByMillsCorporateDashboard
                : getDowntimeSummarySecondaryFailureCodeByMillsCorporateDashboard,
            {
                variables: {
                    start_date: getStartOfDateRange(filters.startDate as Date),
                    end_date: getEndOfDateRange(filters.endDate as Date),
                    start_date_trailing_trend:
                        TrailingTrendPeriodOptionsEnum.ONE_MONTH === filters.trailingTrendPeriod
                            ? moment
                                  .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                                  .subtract(1, "months")
                                  .startOf("day")
                                  .toDate()
                            : TrailingTrendPeriodOptionsEnum.THIRTEEN_WEEK === filters.trailingTrendPeriod
                            ? moment
                                  .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                                  .subtract(13, "weeks")
                                  .startOf("day")
                                  .toDate()
                            : TrailingTrendPeriodOptionsEnum.SIX_MONTH === filters.trailingTrendPeriod
                            ? moment
                                  .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                                  .subtract(6, "months")
                                  .startOf("day")
                                  .toDate()
                            : moment
                                  .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                                  .subtract(1, "years")
                                  .startOf("day")
                                  .toDate(),
                    end_date_trailing_trend: getEndOfDateRange(filters.startDate as Date),
                    where: {
                        _or: [
                            {
                                failure_code: {
                                    _or: [
                                        {
                                            code: { _neq: END_OF_SHIFT_FAILURE_CODE_NAME },
                                        },
                                        {
                                            code: { _is_null: true },
                                        },
                                    ],
                                },
                            },
                            {
                                failure_code_id: { _is_null: true },
                            },
                        ],
                        ...(summaryOption === SummaryOption.BY_CODE
                            ? { failure_code_id: { _eq: id } }
                            : summaryOption === SummaryOption.BY_SECONDARY_CODE
                            ? { secondary_failure_code_id: { _eq: id }, failure_code_id: { _eq: codeId } }
                            : {}),
                        machine_center: {
                            ...(filters.machineCenter?.length! > 0 &&
                                !filters.machineCenter?.includes(allElements) && {
                                    machine_center_id: {
                                        _in: filters.machineCenter,
                                    },
                                }),
                            production_line: {
                                ...(filters.productionLine?.length! > 0 &&
                                    !filters.productionLine?.includes(allElements) && {
                                        production_line_id: {
                                            _in: filters.productionLine,
                                        },
                                    }),
                                mill: {
                                    ...(filters.division !== allElements && {
                                        division_id: {
                                            _eq: filters.division,
                                        },
                                    }),
                                    ...(filters.region !== allElements && {
                                        region_id: {
                                            _eq: filters.region,
                                        },
                                    }),
                                },
                            },
                        },
                    },
                },
            },
        );
        const [downtimeSummaryAllMills, setDowntimeSummaryAllMills] = useState<IDowntimeSummaryTotalsByCodeAllMills[]>(
            [],
        );
        const [downtimeSummaryTrailingPeriod, setDowntimeSummaryTrailingPeriod] = useState<
            IDowntimeSummaryTotalsByCodeAllMills[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.baseDowntimes) {
                const downtimeSummaryByFailureTypeAllMillsArray: IDowntimeSummaryTotalsByCodeAllMills[] =
                    data.baseDowntimes;
                //Here we have to filter the failure types that have seconds null because Hasura doesn't support filtering on aggregate fields directly.
                const downtimeSummaryByFailureTypeAllMillsArrayFiltered: IDowntimeSummaryTotalsByCodeAllMills[] =
                    downtimeSummaryByFailureTypeAllMillsArray.filter((summary) => summary.total_seconds > 0);
                setDowntimeSummaryAllMills(downtimeSummaryByFailureTypeAllMillsArrayFiltered);
                if (data?.trailingTrendDowntimes) {
                    const downtimeSummaryTrailingPeriodArray: IDowntimeSummaryTotalsByCodeAllMills[] =
                        data.trailingTrendDowntimes;
                    //Here we have to filter the failure types that have seconds null because Hasura doesn't support filtering on aggregate fields directly.
                    const downtimeSummaryTrailingPeriodArrayFiltered: IDowntimeSummaryTotalsByCodeAllMills[] =
                        downtimeSummaryTrailingPeriodArray.filter((summary) => summary.total_seconds > 0);
                    setDowntimeSummaryTrailingPeriod(downtimeSummaryTrailingPeriodArrayFiltered);
                }
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return {
            loading,
            downtimeSummaryAllMills,
            downtimeSummaryTrailingPeriod,
            apolloQueryError,
            refetch,
        };
    },
    GetCorporateAllDivisionAllRegionsAllMills: () => {
        const { loading, error, data, refetch } = useQuery(getCorporateAllDivisionAllRegionsAllMills);
        const [corporations, setCorporations] = useState<ICorporation[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.corporate) {
                const corporationsArray: ICorporation[] = data.corporate;
                setCorporations(corporationsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, corporations, error: apolloQueryError, refetch };
    },
    GetAllKeySuccessFactors: () => {
        const { loading, error, data } = useQuery(getAllKeySuccessFactors);
        const [keySuccessFactors, setKeySuccessFactors] = useState<IKeySuccessFactor[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.key_success_factors) {
                const keySuccessFactorsArray: IKeySuccessFactor[] = data.key_success_factors;
                setKeySuccessFactors(keySuccessFactorsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, keySuccessFactors, error: apolloQueryError };
    },
    GetAllFailureTypes: () => {
        const { loading, error, data } = useQuery(getAllFailureTypes);
        const [failureTypes, setFailureTypes] = useState<IFailureType[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.failure_type) {
                const failureTypesArray: IFailureType[] = data.failure_type;
                setFailureTypes(failureTypesArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, failureTypes, error: apolloQueryError };
    },
    UpdateCorporateDashboardFeature: () => {
        const [updateCorporateDashboard, { error, loading }] = useMutation(updateCorporateDashboardFeature);

        const activateCorporateDashboard = useCallback(
            async (corporateId: number) =>
                await updateCorporateDashboard({
                    variables: {
                        corporate_id: corporateId,
                        corporate_dashboard_enabled: true,
                    },
                }),
            [updateCorporateDashboard],
        );

        const deactivateCorporateDashboard = useCallback(
            async (corporateId: number) =>
                await updateCorporateDashboard({
                    variables: {
                        corporate_id: corporateId,
                        corporate_dashboard_enabled: false,
                    },
                }),
            [updateCorporateDashboard],
        );

        return { error, activateCorporateDashboard, deactivateCorporateDashboard, loading };
    },
};
